import { apiClient as api } from './api';

/**
 * Service to handle document-project relationships
 */

/**
 * Get all documents associated with a project
 * @param {number} projectId - The project ID
 * @returns {Promise} - The API response
 */
export const getProjectDocuments = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}/documents`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching project documents: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

/**
 * Get all documents accessible to the current user
 * @param {number} [projectId] - Optional project ID to filter by
 * @param {boolean} [latestOnly=true] - Whether to show only latest versions
 * @returns {Promise} - The API response
 */
export const getUserDocuments = async (projectId = null, latestOnly = true) => {
  try {
    // Use correct endpoint and path
    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    params.append('latest_only', latestOnly.toString());
    
    const url = `/documents?${params.toString()}`;
    console.log("Fetching documents from:", url);
    const response = await api.get(url);
    console.log("Response:", response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user documents: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.message || error.message,
      documents: [] // Provide empty array to prevent undefined errors
    };
  }
};

/**
 * Update a document's project association
 * @param {string} documentId - The document ID
 * @param {number} projectId - The project ID to associate with
 * @returns {Promise} - The API response
 */
export const updateDocumentProject = async (documentId, projectId) => {
  try {
    const response = await api.put(`/documents/${documentId}/project`, {
      project_id: projectId
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating document project: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

/**
 * Save a document with project association
 * @param {Object} documentData - The document data
 * @returns {Promise} - The API response
 */
export const saveDocumentWithProject = async (documentData) => {
  try {
    const response = await api.post('/documents', documentData);
    return response.data;
  } catch (error) {
    console.error(`Error saving document: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};

/**
 * Check if a document exists by ID
 * @param {string} documentId - The document ID to check
 * @returns {Promise<Object>} - Contains success flag and document data if found
 */
export const checkDocumentExists = async (documentId) => {
  try {
    const response = await api.get(`/get-document/${documentId}`);
    return {
      success: true,
      exists: true,
      document: response.data
    };
  } catch (error) {
    if (error.response?.status === 404) {
      return {
        success: true,
        exists: false
      };
    }
    return {
      success: false,
      error: error.response?.data?.message || error.message
    };
  }
};